import "../HomeTax/HomeTax.css";
import "./GruposResponsabilidade.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { AppDispatch, RootState } from "../../redux/store";
import {
  createGroup,
  deleteGroup,
  getGroups,
  setGroupAdministrator,
} from "../../redux/actions/fluxoNSimilar";
import HeaderProtected from "../../componentes/headerProtected/header";
import Sidebar from "../../componentes/sideBar/SideBar";
import { SupportFooter } from "../../componentes/supportFooter/supportFooter";
import HeadingConfigGrupos from "../../componentes/ConfigGrupos/HeadingConfigGrupos";
import GenericGrid from "../../componentes/fluxo-engenheiro/grid/grid";
import { SkeletonWrapper } from "../../componentes/SkeletonWrapper/sleketonWrapper";
import { ModalAprover } from "../../componentes/fluxo-tax/modalAprover/modalAprover";
import { usePaginationState } from "../../hooks/usePaginationState";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";
import { useModalState } from "../../hooks/useModalState";
import { useNotification } from "../../hooks/useNotification";
import { buildGruposResponsabilidadeColumns } from "../../utils/DataGrid/gruposResponsabilidadeColumns";
import Textnotifications from "../../utils/notifications";
import functionDate from "../../utils/formatarData";
import { ResponsibilityGroupItem } from "../../types/gruposResponsabilidade";
import Lixo from "../../assets/svgs/lixo.svg";
import EyesButtonIcon from "../../assets/svgs/eyesButtonIcon.svg";
import ModalNovoGrupo from "../../componentes/ConfigGrupos/ModalNovoGrupo";
import Notification from "../../componentes/fluxo-tax/Notification/notification";
import SucessIcon from "../../assets/svgs/sucess.svg";
import IconAlert from "../../assets/svgs/icon-alert.svg";

const GruposResponsabilidade = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { groups } = useSelector((state: RootState) => state.sessionGroups);

  const paginationState = usePaginationState();
  const persistedPagination = usePersistedPagination(
    "pagination_grupos_responsabilidade",
    { page: 1, pageSize: 20 },
  );
  const pagination = { ...paginationState, ...persistedPagination };
  const deleteModal = useModalState();
  const editAdminModal = useModalState();
  const newGroupModal = useModalState();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] =
    useState<ResponsibilityGroupItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notificationIcon, setNotificationIcon] = useState(SucessIcon);
  const {
    notification,
    labelNotification,
    backgroundColor,
    setNotification,
    showNotification,
  } = useNotification();

  useEffect(() => {
    dispatch(getGroups({ page: pagination.page, pageSize: pagination.pageSize }));
  }, [dispatch, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (!groups) return;
    const groupItems = Array.isArray(groups.content) ? groups.content : [];

    pagination.setRows(
      groupItems.map((item) => ({ ...item, classificationId: item.id })),
    );
    pagination.setTotalPages(groups.page?.totalPages ?? 0);
  }, [groups]);

  const handleDelete = async () => {
    if (!selectedId) return;
    setIsSubmitting(true);

    try {
      await dispatch(deleteGroup(selectedId)).unwrap();
      await dispatch(
        getGroups({ page: pagination.page, pageSize: pagination.pageSize }),
      );
      setNotificationIcon(SucessIcon);
      showNotification(
        selectedGroupName
          ? `Grupo "${selectedGroupName}" foi excluído com sucesso!`
          : "Grupo foi excluído com sucesso!",
      );
      deleteModal.close();
      setSelectedId(null);
      setSelectedGroupName(null);
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getDeleteGroupError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao excluir grupo de responsabilidade:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateGroup = async (data: {
    name?: string;
    engineerResponsibleId: string;
  }) => {
    setIsSubmitting(true);

    try {
      await dispatch(
        createGroup({
          name: data.name ?? "",
          engineerResponsibleId: data.engineerResponsibleId,
        }),
      ).unwrap();
      await dispatch(
        getGroups({ page: pagination.page, pageSize: pagination.pageSize }),
      );
      setNotificationIcon(SucessIcon);
      showNotification("Grupo criado com sucesso.");
      newGroupModal.close();
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getCreateGroupError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao criar grupo de responsabilidade:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (data: {
    engineerResponsibleId: string;
  }) => {
    if (!selectedGroup) return;
    setIsSubmitting(true);

    try {
      await dispatch(
        setGroupAdministrator({
          groupId: selectedGroup.id,
          newAdministratorId: data.engineerResponsibleId,
        }),
      ).unwrap();
      setNotificationIcon(SucessIcon);
      showNotification("Administrador atualizado com sucesso.");
      await dispatch(
        getGroups({ page: pagination.page, pageSize: pagination.pageSize }),
      );
      editAdminModal.close();
      setSelectedGroup(null);
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getSetGroupAdministratorError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao atualizar administrador do grupo:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = buildGruposResponsabilidadeColumns({
    renderActions: (row) => (
      <div className="groupActions">
        <Tooltip title="Alterar Admin">
          <button
            className="groupActionButton"
            type="button"
            onClick={() => {
              setSelectedGroup(row);
              editAdminModal.open();
            }}
          >
            Alterar Admin
          </button>
        </Tooltip>
        <Tooltip title="Ver detalhes">
          <button
            className="btn-delete"
            type="button"
            onClick={() => {
              navigate(
                `/config-grupos/${row.id}?from=grupos-responsabilidade`,
              );
            }}
          >
            <img
              src={EyesButtonIcon}
              alt="Ver detalhes"
              width={16}
              className="groupDetailsIcon"
            />
          </button>
        </Tooltip>
        <Tooltip title="Excluir">
          <button
            className="btn-delete"
            type="button"
            onClick={() => {
              setSelectedId(row.id);
              setSelectedGroupName(row.name);
              deleteModal.open();
            }}
          >
            <img src={Lixo} alt="Excluir" width={16} />
          </button>
        </Tooltip>
      </div>
    ),
  });

  return (
    <>
      <Sidebar />
      <HeaderProtected />
      <main className="homeMain">
        <HeadingConfigGrupos
          title="Grupos de Responsabilidade de Laudo"
          description="Crie grupos e defina o Administrador de Engenharia responsável por cada um."
          buttonTitle="Novo grupo"
          onNewGroup={newGroupModal.open}
        />

        <SkeletonWrapper loading={pagination.loading} />

        {!pagination.loading && (
          <div className="containerRequest">
            <GenericGrid
              titleGrid=" "
              rows={pagination.rows}
              totalPages={pagination.totalPages}
              page={pagination.page}
              columns={columns}
              fullWidth={true}
              setCurrentPage={pagination.setPage}
              setLoading={pagination.setLoading}
              setPage={pagination.setPage}
              enablePageSizeSelector={true}
              pageSize={pagination.pageSize}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              onPageSizeChange={(size) => {
                pagination.setPageSize(size);
                pagination.setPage(1);
              }}
            />
          </div>
        )}
      </main>

      {deleteModal.isOpen && (
        <ModalAprover
          titleModal="Excluir Grupo"
          titleDescription={
            <div style={{ marginBottom: 20, marginTop: 15 }}>
              <div
                className="titleAprover"
                dangerouslySetInnerHTML={{
                  __html: functionDate.formatBodyText(
                    Textnotifications.getDeleteGeneric(
                      selectedGroupName
                        ? `Você deseja excluir "${selectedGroupName}"?`
                        : "Você deseja excluir esse grupo?",
                    ),
                  ),
                }}
              />
              <p className="deleteGroupWarning">
                Ao excluir este grupo, todas as regras e os materiais vinculados
                a ele também serão excluídos permanentemente. Essa ação poderá
                afetar a visualização de alguns materiais e não poderá ser
                desfeita.
              </p>
            </div>
          }
          bodyModal=""
          buttonOne="Confirmar"
          onConfirm={handleDelete}
          buttonTwo="Cancelar"
          onClose={() => {
            deleteModal.close();
            setSelectedId(null);
            setSelectedGroupName(null);
          }}
          isSubmitting={isSubmitting}
        />
      )}

      <SupportFooter />

      {newGroupModal.isOpen && (
        <ModalNovoGrupo
          isSubmitting={isSubmitting}
          onClose={newGroupModal.close}
          onConfirm={handleCreateGroup}
        />
      )}

      {editAdminModal.isOpen && selectedGroup && (
        <ModalNovoGrupo
          mode="edit-admin"
          groupName={selectedGroup.name}
          currentAdmin={selectedGroup.administrator}
          isSubmitting={isSubmitting}
          onClose={() => {
            editAdminModal.close();
            setSelectedGroup(null);
          }}
          onConfirm={handleUpdateAdmin}
        />
      )}

      {notification && (
        <Notification
          label={labelNotification}
          backgroundError={backgroundColor}
          setNotification={setNotification}
          img={notificationIcon}
        />
      )}
    </>
  );
};

export default GruposResponsabilidade;